import { ChangeDetectionStrategy, Component, effect, inject, untracked } from '@angular/core';
import {
    ActivationEnd,
    NavigationCancel,
    NavigationEnd,
    NavigationSkipped,
    NavigationStart,
    Router,
    RouterEvent,
    RouterOutlet,
} from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslationService } from './core/i18n/translation.service';
import { locale as itLang } from './core/i18n/vocabs/it';
import { locale as enLang } from './core/i18n/vocabs/en';
import { OnboardingFacade } from './facade/onboarding-facade';
import { getConfigInfoToast } from './core/helpers/config-toast';
import { PrimeNG } from 'primeng/config';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterOutlet, ToastModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <p-toast key="guard" />
        <router-outlet />
    `,
})
export class AppComponent {
    private readonly translationService = inject(TranslationService);
    private readonly translateService = inject(TranslateService);
    private readonly facade = inject(OnboardingFacade);
    private readonly router = inject(Router);
    private readonly messageService = inject(MessageService);
    private readonly primeng = inject(PrimeNG);

    private readonly routerEvent = toSignal(
        this.router.events.pipe(filter((event) => this.isRouterEventsClassesNeeded(event as RouterEvent))),
    );

    constructor() {
        this.translationService.loadTranslations(itLang, enLang);
        this.translateService.get('primeng').subscribe((res) => this.primeng.setTranslation(res));

        effect(() => {
            const event = this.routerEvent();
            untracked(() => {
                if (event instanceof NavigationStart && !this.facade.getKeepToastMessage()()) {
                    this.facade.clearMessages();
                }
                if (event instanceof NavigationEnd) {
                    this.facade.setKeepToastMessage(false);
                    const pendingMessage = this.facade.getPendingToastMessage()();
                    if (pendingMessage) {
                        this.messageService.add({ ...getConfigInfoToast(pendingMessage), key: 'guard' });
                        this.facade.setPendingToastMessage(null);
                    }
                }
            });
        });
    }

    private isRouterEventsClassesNeeded(event: Event | RouterEvent): boolean {
        return (
            event instanceof NavigationStart ||
            event instanceof ActivationEnd ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationSkipped
        );
    }
}
